'use client';

import React, { useEffect, useRef, useId } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '../lib/utils';

export interface SheetProps {
  open: boolean;
  onClose: () => void;
  /** Which edge the sheet slides in from. */
  side?: 'right' | 'left';
  /** Sheet title. Rendered in the header. */
  title?: string;
  /** Optional description below the title. */
  description?: string;
  /** Width preset. */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Sheet body content. */
  children: React.ReactNode;
  /** Optional footer region (sticky at bottom). */
  footer?: React.ReactNode;
  /** Close on backdrop click. Default true. */
  closeOnBackdrop?: boolean;
  /** Close on Escape. Default true. */
  closeOnEscape?: boolean;
  /** Show X close button. Default true. */
  showCloseButton?: boolean;
  /** Extra classes applied to the sheet panel. */
  className?: string;
}

const sizeClasses: Record<'sm' | 'md' | 'lg' | 'xl', string> = {
  sm: 'w-full sm:max-w-sm',
  md: 'w-full sm:max-w-md',
  lg: 'w-full sm:max-w-lg',
  xl: 'w-full sm:max-w-2xl',
};

/**
 * Side-sliding panel — used for secondary context (e.g., "Show guide",
 * inline record details, filters panel). Keeps primary surface visible.
 *
 * Pattern-wise a close cousin of Dialog but horizontally anchored. Uses
 * the same focus trap and Escape-to-close conventions.
 */
export function Sheet({
  open,
  onClose,
  side = 'right',
  title,
  description,
  size = 'md',
  children,
  footer,
  closeOnBackdrop = true,
  closeOnEscape = true,
  showCloseButton = true,
  className,
}: SheetProps) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  const closeOnEscapeRef = useRef(closeOnEscape);
  const sheetId = useId();
  const titleId = `${sheetId}-title`;
  const descId = `${sheetId}-description`;

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    closeOnEscapeRef.current = closeOnEscape;
  }, [closeOnEscape]);

  useEffect(() => {
    if (!open) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (closeOnEscapeRef.current && e.key === 'Escape') {
        onCloseRef.current();
        return;
      }
      if (e.key === 'Tab') {
        const focusable = sheetRef.current?.querySelectorAll(
          'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (!focusable?.length) return;
        const first = focusable[0] as HTMLElement;
        const last = focusable[focusable.length - 1] as HTMLElement;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    previousFocusRef.current = document.activeElement as HTMLElement;
    document.body.style.overflow = 'hidden';
    const focusFrame = requestAnimationFrame(() => {
      sheetRef.current?.focus();
    });

    return () => {
      cancelAnimationFrame(focusFrame);
      document.body.style.overflow = '';
      previousFocusRef.current?.focus();
    };
  }, [open]);

  if (!open) return null;

  const sideClasses =
    side === 'right'
      ? 'right-0 border-l animate-in slide-in-from-right duration-200'
      : 'left-0 border-r animate-in slide-in-from-left duration-200';

  const content = (
    <div
      className="fixed inset-0 z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? titleId : undefined}
      aria-describedby={description ? descId : undefined}
    >
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm animate-in fade-in-0 duration-200"
        onClick={closeOnBackdrop ? onClose : undefined}
        aria-hidden="true"
      />
      <div
        ref={sheetRef}
        tabIndex={-1}
        className={cn(
          'fixed inset-y-0 flex flex-col bg-white shadow-2xl border-slate-200',
          sideClasses,
          sizeClasses[size],
          className,
        )}
      >
        {(title || showCloseButton) && (
          <div className="flex shrink-0 items-start justify-between border-b border-slate-200 px-6 py-4">
            <div className="min-w-0 flex-1">
              {title && (
                <h2 id={titleId} className="font-heading text-lg font-semibold text-statura-ink">
                  {title}
                </h2>
              )}
              {description && (
                <p id={descId} className="mt-1 text-sm text-slate-600">
                  {description}
                </p>
              )}
            </div>
            {showCloseButton && (
              <button
                type="button"
                onClick={onClose}
                className="ml-4 inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-statura-ink"
                aria-label="Close"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            )}
          </div>
        )}
        <div className="flex-1 overflow-y-auto px-6 py-4">{children}</div>
        {footer && (
          <div className="shrink-0 border-t border-slate-200 bg-slate-50 px-6 py-4">{footer}</div>
        )}
      </div>
    </div>
  );

  if (typeof window === 'undefined') return null;
  return createPortal(content, document.body);
}
